import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { 
  X, Users, List, MessageSquare, LogOut, Loader2, 
  Home, Radio, BookOpen, ChevronDown, Sparkles, Scale, Stethoscope 
} from 'lucide-react'; // 🟢 Added missing icons for the dropdown
import type { UserProfile } from './UserNavbar'; 
import logo from '../../assets/trueAdvisoLogo/trueAdvisologofull.jpeg';

interface UserPhoneSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  profile?: UserProfile | null;
  logout: () => void;
  isLoggingOut: boolean;
}

export default function UserPhoneSidebar({ 
  isOpen, 
  onClose, 
  profile, 
  logout, 
  isLoggingOut 
}: UserPhoneSidebarProps) {
  const firstName = profile?.name ? profile.name.split(' ')[0] : 'User';

  // 🟢 State to control the dropdown accordion for Experts
  const [isExpertsOpen, setIsExpertsOpen] = useState(false);

  // 🟢 Standard menu items (Experts handled separately for the dropdown)
  const menuItems = [
    { label: 'Categories', to: '/categories', icon: List },
    { label: 'Live Sessions', to: '/live-sessions', icon: Radio },
    { label: 'Chats', to: '/user/messages', icon: MessageSquare },
    { label: 'Blogs', to: '/blogs', icon: BookOpen },
  ];

  // 🟢 Subcategories for the Experts dropdown
  const expertCategories = [
    { label: 'Astrology', to: '/experts/astrology', icon: Sparkles },
    { label: 'Lawyers', to: '/experts/lawyer', icon: Scale },
    { label: 'Doctors', to: '/experts/doctor', icon: Stethoscope },
  ];

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium transition-all duration-200 group ${
      isActive
        ? 'text-blue-700 bg-blue-50 translate-x-1'
        : 'text-gray-600 hover:text-blue-700 hover:bg-blue-100 hover:translate-x-1'
    }`;

  return (
    <>
      <div 
        className={`fixed inset-0 bg-black/50 z-[60] transition-opacity duration-300 lg:hidden ${
          isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
        onClick={onClose}
      />

      <div 
        className={`fixed top-0 left-0 h-full w-[280px] bg-white z-[70] shadow-2xl transform transition-transform duration-300 ease-in-out flex flex-col lg:hidden ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <img src={logo} alt="trueAdviso Logo" className="h-8 w-auto object-contain" />
          
          <button 
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-red-500 rounded-full transition-colors duration-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 border-b border-gray-100 flex items-center gap-3">
          {profile?.profilePhotoUrl ? (
            <img 
              src={profile.profilePhotoUrl} 
              alt="Profile" 
              className="w-12 h-12 rounded-full object-cover border border-gray-200" 
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-cyan-500 to-blue-600 flex items-center justify-center text-white text-lg font-bold">
              {firstName[0]}
            </div>
          )}
          <div>
            <p className="font-semibold text-gray-800">{profile?.name || 'User'}</p>
            <p className="text-sm text-gray-500">{profile?.userCode || 'Welcome back'}</p>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {/* 🟢 1. Home Link */}
          <NavLink to="/" onClick={onClose} className={navClass}>
            <Home className="w-5 h-5 transition-transform duration-200 group-hover:scale-110" />
            Home
          </NavLink>

          {/* 🟢 2. Experts Interactive Dropdown */}
          <div>
            <button
              onClick={() => setIsExpertsOpen(!isExpertsOpen)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-base font-medium text-gray-600 hover:text-blue-700 hover:bg-blue-100 transition-all duration-200 group"
            >
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 transition-transform duration-200 group-hover:scale-110" />
                <span>Experts</span>
              </div>
              <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isExpertsOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown Menu Items */}
            {isExpertsOpen && (
              <div className="pl-4 mt-1 space-y-1 border-l-2 border-blue-100 ml-4 mb-2">
                {expertCategories.map((category) => (
                  <NavLink
                    key={category.label}
                    to={category.to}
                    onClick={onClose}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        isActive ? 'text-blue-700 bg-blue-50 font-semibold' : 'text-gray-500 hover:text-blue-600 hover:bg-gray-50'
                      }`
                    }
                  >
                    <category.icon className="w-4 h-4" />
                    <span>{category.label}</span>
                  </NavLink>
                ))}
              </div>
            )}
          </div>

          {/* 🟢 3. Rest of the standard menu items */}
          {menuItems.map((item) => (
            <NavLink key={item.label} to={item.to} onClick={onClose} className={navClass}>
              <item.icon className="w-5 h-5 transition-transform duration-200 group-hover:scale-110" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-gray-100">
          <button
            onClick={logout}
            disabled={isLoggingOut}
            className="flex items-center gap-3 w-full px-4 py-3 text-red-600 bg-transparent hover:bg-red-600 hover:text-white rounded-xl transition-all duration-300 group disabled:opacity-50"
          >
            <div className="transition-transform duration-200 group-hover:scale-110">
              {isLoggingOut ? <Loader2 className="w-5 h-5 animate-spin" /> : <LogOut className="w-5 h-5" />}
            </div>
            <span className="font-semibold">{isLoggingOut ? 'Logging out...' : 'Logout'}</span>
          </button>
        </div>
      </div>
    </>
  );
}